import { Spin } from 'antd';
import { isArray, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom';
import styled from 'styled-components';
import { Arrow_Down_2, Arrow_Up_2, avatar, Compare_2, homepage_icon } from '../../../assets/img';
import { BreadCrumb, ModelTC } from '../../../components';
import GlobalAlertMessage from '../../../components/GlobalAlertMessage/GlobalAlertMessage';
import ProposalSetting from '../../../components/ProposalSetting/ProposalSetting';
import SuccessorComparison from '../../../components/SuccessorComparison/SuccessorComparison';
import { useShowPopupSaveAgenda } from '../../../hooks/useShowPopupSaveAgenda';
import { useUpdatePdcMobilityStepNumber } from '../../../hooks/useUpdatePdcMobilityStepNumber';
import { pdcMeetingApi } from '../../../services/pdcMeetingApi';
import {
  NOTIFICATION_TYPE,
  PROPOSAL_LABEL,
  PROPOSAL_SELECT_OPTIONS,
  PROPOSAL_TYPES,
  SELECT_ALL_LABEL,
  USER_ACCESS_MESSAGE,
  CONTRACT_TALENTS_SALARY_GRADE,
} from '../../../utils/constants';
import { showNotification } from '../../../utils/notification';
import MobilityBotButtonControls from '../components/MobilityButtonControls/MobilityBotButtonControls';
import MobilityTopButtonControls from '../components/MobilityButtonControls/MobilityTopButtonControls';
import MobilityFinalizeBar from '../components/MobilityFinalizeBar/MobilityFinalizeBar';
import PdcMobilityProcessBar from '../components/PdcMobilityProcessBar/PdcMobilityProcessBar';
import PdcProposalDetail from './components/PdcProposalItem/PdcProposalDetail';
import * as styles from './pdc-proposal-list.module.scss';
import usePDCMeetingMobilityAgendaUrl from '../../../hooks/usePDCMeetingMobilityAgendaUrl';

const CustomBtn = styled.button`
  border: none;
  background: transparent;
  padding: 5px 7px;

  img {
    width: 24px;
    height: 24px;
  }
`;

const PdcProposalList = (props) => {
  const history = useHistory();
  const { idMeeting, mobilityId, proposalId } = useParams();
  const userInfos = useSelector((state) => state.user);
  const { roleId } = useSelector((state) => state.user.roleActive);
  const [loading, setLoading] = useState(false);
  const [proposalList, setProposalList] = useState([]);
  const [itemExpand, setItemExpand] = useState(null);
  const [compareItem, setCompareItem] = useState(null);
  const [initProposalOptions, setInitProposalOptions] = useState([]);
  const [proposalType, setProposalType] = useState(null);
  const [proposalTypeName, setProposalTypeName] = useState('');
  const [proposalItemList, setProposalItemList] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [isTemplateContentOpen, setIsTemplateContentOpen] = useState(false);
  const [openStaffId, setOpenStaffId] = useState(null);
  const [isBack, setIsBack] = useState(false);
  const [compareData, setCompareData] = useState({
    initData: [],
    currentData: [],
  });
  const [isOpenSettingHasType, setIsOpenSettingHasType] = useState(false);
  const [currentOpenSettingProposal, setCurrentOpenSettingProposal] = useState(null);

  const mobilityAgendaUrl = usePDCMeetingMobilityAgendaUrl(idMeeting);
  const BREAD_CRUMB = [
    { name: 'Homepage', icon: homepage_icon, url: '/homepage' },
    { name: 'PDC Meeting', url: '/pdc' },
    { name: 'Mobility Agenda', url: mobilityAgendaUrl },
    { name: 'Proposal' },
  ];

  // Get the condition to edit mobility agenda
  const {
    isOnlyViewAgenda,
    isShowTopButtons,
    isShowBotButtons,
    isAdditionalViewAccess,
    meetingDetail,
    mobilityDetails,
    isEnableMobilityTab,
    proposalMainStep,
    fetchMobilityDetails,
    isShownMobilityTabBar,
    isHRPartners,
    isChairMainOrCommiteeOrAdvocator,
  } = props;
  const { updateStepNumber } = useUpdatePdcMobilityStepNumber();

  // Update current data to compare value
  useEffect(() => {
    setCompareData((prev) => ({
      ...prev,
      currentData: proposalList,
    }));
  }, [proposalList]);

  const isChangeProposalList = useMemo(() => {
    return JSON.stringify(compareData.initData) !== JSON.stringify(compareData.currentData);
  }, [compareData]);

  const { isShowPopupSaveAgenda, handleKeepNavigate, setIsShowPopupSaveAgenda } = useShowPopupSaveAgenda(isChangeProposalList && !isBack);

  const fetchProposalList = useCallback(async () => {
    if (!mobilityId) return;
    try {
      setLoading(true);
      const res = await pdcMeetingApi.getProposalList(mobilityId, proposalId);
      if (res.status === 200) {
        const tempData = !isEmpty(res?.data?.result) ? res.data.result.map((i) => ({ ...i, proposal_item_list: i?.proposal_settings || [] })) : [];
        const tempDataHassuccessors = isArray(tempData)
          ? tempData.map((item) => ({
              ...item,
              proposal_compare_successors: isArray(item?.proposal_compare_successors)
                ? item?.proposal_compare_successors.map((successor) => ({
                    ...successor,
                    ...(successor?.staffInfo || {}),
                    experiences_checked: !isEmpty(successor?.experiences) ? successor?.experiences.filter((ex) => ex?.checked) : [],
                  }))
                : [],
            }))
          : [];
        setProposalList(tempDataHassuccessors);
        setCompareData((prev) => ({
          ...prev,
          initData: tempDataHassuccessors,
          currentData: tempDataHassuccessors,
        }));
      }
    } catch (error) {
      console.error(error);
      showNotification(USER_ACCESS_MESSAGE.AN_UNEXPECTED_ERROR, NOTIFICATION_TYPE.FAILED);
    } finally {
      setLoading(false);
    }
  }, [mobilityId]);

  // Fetch mobility in the first time
  useEffect(() => {
    fetchProposalList();
  }, [fetchProposalList]);

  // Fetch proposal type
  useEffect(() => {
    const fetchProposalTypes = async () => {
      try {
        const resProposalType = await pdcMeetingApi.getListProposalType();
        if (resProposalType.status === 200) {
          const tempData = resProposalType?.data?.result;
          setInitProposalOptions(
            !isEmpty(tempData)
              ? tempData.map((item) => ({
                  label: <span>{item.category}</span>,
                  title: item.category,
                  options: !isEmpty(item.proposal_types)
                    ? item.proposal_types.map((option) => ({
                        label: <span>{option.name}</span>,
                        value: option.name,
                      }))
                    : [],
                }))
              : [],
          );
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchProposalTypes();
  }, []);

  // Fetch proposal item list
  const flattenProposalItems = (data) => {
    const result = [];
    const traverse = (items) => {
      items?.forEach((item) => {
        result.push({
          label: item.label,
          value: item.value,
          checked: item.checked,
        });

        if (Array.isArray(item.children)) {
          traverse(item.children);
        }
      });
    };
    traverse(data);
    return result;
  };

  // Helper function to merge proposal settings while preserving user input
  const mergeProposalSettings = (existingSettings, newSettings) => {
    if (!newSettings) return newSettings;
    // If no existing settings, just return the new settings (first time setting proposal type)
    if (!existingSettings) return newSettings;

    // Merge a section by only preserving values for keys that exist in newSection
    const mergeSection = (existingSection, newSection) => {
      if (!newSection || typeof newSection !== 'object') return newSection;

      const merged = { ...newSection };

      // Preserve existing values only for keys present in the new section
      Object.keys(newSection).forEach((key) => {
        const existingValue = existingSection?.[key];
        // Preserve if existing value is truthy, or if it's explicitly false/0 (for toggles)
        if (existingValue !== undefined && existingValue !== null && existingValue !== '') {
          merged[key] = existingValue;
        }
      });

      return merged;
    };

    return {
      ...newSettings,
      justificationInfo: mergeSection(existingSettings.justificationInfo, newSettings.justificationInfo),
      decisionInfo: mergeSection(existingSettings.decisionInfo, newSettings.decisionInfo),
      staffInfo: mergeSection(existingSettings.staffInfo, newSettings.staffInfo),
      positionInfo: mergeSection(existingSettings.positionInfo, newSettings.positionInfo),
    };
  };

  useEffect(() => {
    if (!proposalType || isOpenSettingHasType) return;

    const fetchProposalItemList = async () => {
      try {
        setLoading(true);
        const res = await pdcMeetingApi.getProposalItemList(proposalType);
        if (res.status === 200) {
          const arr = [{ label: SELECT_ALL_LABEL, value: PROPOSAL_LABEL.SELECT_ALL, checked: false }, ...flattenProposalItems(res.data?.result)];
          // eslint-disable-next-line no-unused-vars
          if (CONTRACT_TALENTS_SALARY_GRADE.some((salaryGrade) => (currentOpenSettingProposal?.sg || itemExpand?.sg)?.includes(salaryGrade))) {
            setProposalItemList(
              (arr.some((item) => item.value === PROPOSAL_LABEL.STAFF_DETAILS_CONTRACT_END_DATE)
                ? arr
                : [...arr, { label: 'Contract End Date', value: PROPOSAL_LABEL.STAFF_DETAILS_CONTRACT_END_DATE, checked: true }]
              )
                .map((item) => {
                  if (item.value === PROPOSAL_LABEL.RETIREMENT_DATE) {
                    return { ...item, checked: false };
                  }
                  return item;
                })
                .filter((i) => i.checked)
                .map(({ ...m }) => m),
            );
          } else {
            setProposalItemList(arr.filter((i) => i.checked).map(({ ...m }) => m));
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchProposalItemList();
  }, [proposalType, isOpenSettingHasType]);

  const handleReturnProposalItemList = (dataList) => {
    const isValid = !isEmpty(dataList) ? isEmpty(dataList[0]?.children) : false;

    const markChecked = (item) => ({
      ...item,
      checked: !!dataList.find((f) => f.value === item.value),
    });

    const processChildren = (items = []) =>
      items.map((item) => {
        const updatedItem = markChecked(item);
        if (!isEmpty(item.children)) {
          return {
            ...updatedItem,
            children: processChildren(item.children),
          };
        }
        return updatedItem;
      });

    return isValid ? processChildren(PROPOSAL_SELECT_OPTIONS.slice(1)) : dataList;
  };

  // Fetch template content
  const fetchTemplateContent = async (iExpandItem, settings, isApplied) => {
    if (isEmpty(settings) || !iExpandItem || !isApplied) {
      return;
    }

    setIsTemplateContentOpen(true);
    setOpenStaffId(iExpandItem?.staff_id);

    try {
      setLoading(true);
      const res = await pdcMeetingApi.getTemplateContent({
        staff_id: iExpandItem?.staff_id,
        position_code: iExpandItem?.position_code,
        mobility_id: iExpandItem?.mobility_id,
        mobility_proposal_id: proposalId,
        data: handleReturnProposalItemList(settings),
        roleId,
        proposal_type: iExpandItem?.proposal_type,
      });

      if (res.status === 200) {
        setProposalList((prev) => {
          return !isEmpty(prev)
            ? prev.map((item) => {
                if (item?.staff_id === iExpandItem?.staff_id) {
                  // Merge new settings with existing user input
                  // Preserve proposal_item_list: use settings parameter (which is the proposal_item_list array)
                  // or fallback to existing item.proposal_item_list if settings is not provided
                  const preservedProposalItemList = Array.isArray(settings) ? settings : item?.proposal_item_list || [];

                  return {
                    ...item,
                    proposal_settings: mergeProposalSettings(item?.proposal_settings, res?.data?.result),
                    proposal_item_list: preservedProposalItemList, // Preserve proposal_item_list for isProposalItemSelected checks
                  };
                }
                return item;
              })
            : [];
        });

        setCompareData((prev) => ({
          ...prev,
          initData: isArray(prev?.initData)
            ? prev.initData.map((i) => {
                if (i?.staff_id === iExpandItem?.staff_id) {
                  // Merge new settings with existing user input
                  // Preserve proposal_item_list: use settings parameter (which is the proposal_item_list array)
                  // or fallback to existing i.proposal_item_list if settings is not provided
                  const preservedProposalItemList = Array.isArray(settings) ? settings : i?.proposal_item_list || [];
                  return {
                    ...i,
                    proposal_settings: mergeProposalSettings(i?.proposal_settings, res?.data?.result),
                    proposal_item_list: preservedProposalItemList, // Preserve proposal_item_list for isProposalItemSelected checks
                  };
                }
                return i;
              })
            : [],
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickBack = async () => {
    setIsBack(true);
    if (isChangeProposalList) {
      await handleSaveAsDraft();
    }
    history.push(`/pdc-meeting/${idMeeting}/mobility/${mobilityId}`);
  };

  const handleSaveBeforeExit = async () => {
    setIsBack(true);
    setIsShowPopupSaveAgenda(false);
    await handleSaveAsDraft();
    handleKeepNavigate();
  };

  const handleSaveAsDraft = async () => {
    if (!roleId) return;

    try {
      setLoading(true);
      const payload = !isEmpty(proposalList)
        ? proposalList.map((item, index) => {
            const tempSuccessor = item?.proposal_settings?.positionInfo?.identifiedSuccessors?.successorList;
            const tempReplacement = item?.proposal_settings?.staffInfo?.replacement?.replacement_staff_ids;
            const tempFirstLineSuccessor = item?.proposal_settings?.staffInfo?.successor_to_1st_line;
            const tempSecondLineSuccessor = item?.proposal_settings?.staffInfo?.successor_to_2nd_line;
            const tempThirdLineSuccessor = item?.proposal_settings?.staffInfo?.successor_to_3rd_line;
            return {
              order: index + 1,
              staff_id: item?.staff_id,
              mobility_id: item?.mobility_id,
              mobility_proposal_id: proposalId,
              position_code: item?.position_code,
              proposal_type: item?.proposal_type,
              proposal_type_name: item?.proposal_type_name?.trim(),
              replacement_staff_lists: !isEmpty(tempReplacement) ? tempReplacement.map((i) => i.staffId || i.staff_id) : [],
              replacement_save_status: item?.proposal_settings?.staffInfo?.replacement?.save,
              replacement_input_value: item?.proposal_settings?.staffInfo?.replacement?.replacement_input_value,
              previous_contract_details: item?.proposal_settings?.staffInfo?.previous_contract_details,
              previous_secondment: item?.proposal_settings?.staffInfo?.previous_secondment,
              identified_successor_staff_lists: !isEmpty(tempSuccessor) ? tempSuccessor.map((s) => s.staffId || s.staff_id) : [],
              successor_first_line_position_code_lists: !isEmpty(tempFirstLineSuccessor)
                ? tempFirstLineSuccessor.map((s) => s.position_code || s.position_code)
                : [],
              successor_second_line_position_code_lists: !isEmpty(tempSecondLineSuccessor)
                ? tempSecondLineSuccessor.map((s) => s.position_code || s.position_code)
                : [],
              successor_third_line_position_code_lists: !isEmpty(tempThirdLineSuccessor)
                ? tempThirdLineSuccessor.map((s) => s.position_code || s.position_code)
                : [],
              identified_successor_save_status: item?.proposal_settings?.positionInfo?.identifiedSuccessors?.save,
              identified_successor_input_value: item?.proposal_settings?.positionInfo?.identifiedSuccessors?.identified_successor_input_value,
              justification: item?.proposal_settings?.justificationInfo?.justification,
              decision_remarks: item?.proposal_settings?.decisionInfo?.decision_remarks,
              effective_start_date: item?.proposal_settings?.decisionInfo?.effectiveStartDate,
              effective_end_date: item?.proposal_settings?.decisionInfo?.effectiveEndDate,
              contract_start_date: item?.proposal_settings?.decisionInfo?.contractStartDate,
              contract_end_date: item?.proposal_settings?.decisionInfo?.contractEndDate,
              decision: item?.proposal_settings?.decisionInfo?.decision?.selectedDecision,
              remarks: item?.proposal_settings?.decisionInfo?.remarks,
              proposal_compare_successors: item?.proposal_compare_successors,
              proposal_settings: !isEmpty(item?.proposal_item_list) ? handleReturnProposalItemList(item?.proposal_item_list) : [],
              note: item?.proposal_settings?.staffInfo?.note,
              current_incumbent_input_value: item?.proposal_settings?.positionInfo?.current_incumbent_input_value,
              current_incumbent_toggle: item?.proposal_settings?.positionInfo?.current_incumbent_toggle ? 1 : 0,
            };
          })
        : [];
      const res = await pdcMeetingApi.updateProposalList(payload, roleId, userInfos);
      if (res.status === 200) {
        if (proposalMainStep?.step < 2) {
          await updateStepNumber(mobilityId, 2);
        }
        setCompareData((prev) => ({
          ...prev,
          initData: prev.currentData,
        }));
        return true;
      }
    } catch (error) {
      console.error(error);
      showNotification(USER_ACCESS_MESSAGE.AN_UNEXPECTED_ERROR, NOTIFICATION_TYPE.FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleClickNext = async () => {
    if (proposalMainStep?.step < 2) {
      await updateStepNumber(mobilityId, 2);
    }
    history.push(`/pdc-meeting/${idMeeting}/mobility/${mobilityId}/assessment/${proposalId}?mobilityType=single`);
  };

  const handleReturnExpandCollapseClass = (itemExpand, currentItem) => {
    return itemExpand?.staff_id === currentItem?.staff_id ? styles.expanded : styles.collapsed;
  };

  const fetchTalentDetails = async (record) => {
    try {
      setLoading(true);
      const res = await pdcMeetingApi.getStaffDetails(record?.staffId || record?.staff_id);
      if (res.status === 200) {
        const talentDetails = isArray(res?.data?.result) ? res?.data?.result : [];
        const tempData = !isEmpty(proposalList)
          ? proposalList.map((item) => {
              if (item?.staff_id === record?.staff_id) {
                return {
                  ...item,
                  proposal_compare_successors: !isEmpty(talentDetails)
                    ? talentDetails.map((successor) => {
                        if (successor?.staff_id === record?.staff_id) {
                          return {
                            ...successor,
                            experiences_checked: successor?.experiences || [],
                          };
                        } else {
                          return successor;
                        }
                      })
                    : [],
                };
              } else {
                return item;
              }
            })
          : [];
        setCompareData({
          initData: tempData,
          currentData: tempData,
        });
        setProposalList(tempData);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCompareTalent = async (record) => {
    if (!isEmpty(record?.proposal_compare_successors)) {
      setProposalList((prev) => {
        if (!isEmpty(prev)) {
          return prev.map((item) => {
            if (item?.staff_id === record?.staff_id) {
              return {
                ...item,
                proposal_compare_successors: record.proposal_compare_successors.map((i) => ({
                  ...i,
                  ...(i?.staffInfo || {}),
                  experiences_checked: !isEmpty(i?.experiences) ? i?.experiences.filter((ex) => ex?.checked) : [],
                })),
              };
            } else {
              return item;
            }
          });
        }
      });
    } else {
      await fetchTalentDetails(record);
    }
    setCompareItem(record);
  };

  const handleExpandCollapseItem = async (record) => {
    handleSaveAsDraft();

    setItemExpand(record);

    if (!record?.proposal_type) {
      setProposalItemList([]);
      setProposalType(null);
      setProposalTypeName('');
      setIsTemplateContentOpen(false);
      setOpenStaffId(null);
      return;
    }

    const isCheck = !isEmpty(record?.proposal_item_list) ? isEmpty(record?.proposal_item_list[0]?.children) : false;
    const tempSettings = isCheck ? record?.proposal_item_list : flattenProposalItems(record?.proposal_item_list)?.filter((i) => i.checked);
    setProposalItemList(tempSettings);
    setProposalType(record?.proposal_type);
    setProposalTypeName(record?.proposal_type_name);

    await fetchTemplateContent(record, tempSettings, true);
    setOpenStaffId(record?.staff_id);
    setIsTemplateContentOpen(true);
  };

  const handleOpenSettingProposal = (record) => {
    setCurrentOpenSettingProposal(record);

    if (!record?.proposal_type) {
      setProposalItemList([]);
      setProposalType(null);
      setProposalTypeName('');
      return;
    }

    const isCheck = !isEmpty(record?.proposal_item_list) ? isEmpty(record?.proposal_item_list[0]?.children) : false;
    setIsOpenSettingHasType(true);
    setProposalItemList(isCheck ? record?.proposal_item_list : flattenProposalItems(record?.proposal_item_list)?.filter((i) => i.checked));
    setProposalType(record?.proposal_type);
    setProposalTypeName(record?.proposal_type_name);
  };

  const getProposalPrevValue = (record) => {
    let tempData;
    if (!isEmpty(compareData.initData)) {
      compareData.initData.forEach((i) => {
        if (i?.staff_id === record?.staff_id) {
          tempData = i;
        }
      });
    }
    return tempData;
  };

  return (
    <>
      <div style={{ marginBottom: '15px' }}>
        <BreadCrumb level={7} breadCrumbList={BREAD_CRUMB} />
      </div>
      <GlobalAlertMessage style={{ marginBottom: '20px' }} />
      {isShowTopButtons && (
        <MobilityTopButtonControls
          idMeeting={idMeeting}
          mobilityId={mobilityId}
          mobilityDetails={mobilityDetails}
          fetchMobility={fetchMobilityDetails}
          isAdditionalViewAccess={isAdditionalViewAccess}
          isHRPartners={isHRPartners}
          isChairMainOrCommiteeOrAdvocator={isChairMainOrCommiteeOrAdvocator}
          meetingDetail={meetingDetail}
        />
      )}
      {isShownMobilityTabBar ? (
        <MobilityFinalizeBar mobilityDetails={mobilityDetails} />
      ) : (
        <PdcMobilityProcessBar currentStep={2} mobilityDetails={mobilityDetails} isEnableMobilityTab={isEnableMobilityTab} />
      )}

      <Spin spinning={loading}>
        <div className={styles.proposalWrap}>
          <div className={styles.mainTitle}>Proposal List</div>
          <div className={styles.proposalList}>
            {!isEmpty(proposalList) &&
              proposalList.map((item, index) => (
                <div key={`${item.staff_id}.${index}`} className={styles.proposalItem}>
                  <div className={styles.proposalItemHead}>
                    <h3>Proposal {index + 1}</h3>
                  </div>
                  <div className={`${styles.proposalItemContent} ${handleReturnExpandCollapseClass(itemExpand, item)}`}>
                    <div className={styles.infoLeft}>
                      <div className={styles.avatar}>
                        <img src={item.image || avatar} alt="" />
                      </div>
                      <div className={styles.name} style={{ width: '230px' }}>
                        <span className={styles.fieldLabel}>Name</span>
                        <span className={styles.fieldValue}>{item.birth_name || '-'}</span>
                      </div>
                    </div>
                    <div className={styles.position} style={{ width: '320px' }}>
                      <span className={styles.fieldLabel}>Position</span>
                      <span className={styles.fieldValue}>{item.position_name}</span>
                    </div>
                    <div className={styles.type}>
                      {PROPOSAL_TYPES.OTHER_PROPOSAL === item.proposal_type && item.proposal_type_name ? item.proposal_type_name : item.proposal_type}
                    </div>
                    <div className={styles.listAction}>
                      <CustomBtn>
                        <img src={Compare_2} alt="" onClick={() => handleCompareTalent(item)} />
                      </CustomBtn>
                      {!isOnlyViewAgenda && (
                        <CustomBtn>
                          <ProposalSetting
                            defaultValues={!isEmpty(proposalItemList) ? proposalItemList : []}
                            initProposalOptions={initProposalOptions}
                            setInitProposalOptions={setInitProposalOptions}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            setProposalType={setProposalType}
                            proposalType={proposalType}
                            currentItem={item}
                            setItemExpand={setItemExpand}
                            setProposalList={setProposalList}
                            fetchTemplateContent={fetchTemplateContent}
                            handleOpenSettingProposal={handleOpenSettingProposal}
                            setIsOpenSettingHasType={setIsOpenSettingHasType}
                            proposalTypeName={proposalTypeName}
                            setProposalTypeName={setProposalTypeName}
                          />
                        </CustomBtn>
                      )}
                      <CustomBtn>
                        {(!itemExpand || itemExpand?.staff_id !== item.staff_id) && (
                          <img src={Arrow_Down_2} alt="" onClick={() => handleExpandCollapseItem(item)} />
                        )}
                        {itemExpand?.staff_id === item.staff_id && <img src={Arrow_Up_2} alt="" onClick={() => setItemExpand(null)} />}
                      </CustomBtn>
                    </div>
                  </div>
                  {isTemplateContentOpen && openStaffId === item.staff_id && (
                    <div key={item.staff_id} className={`${styles.proposalItemDetail} ${handleReturnExpandCollapseClass(itemExpand, item)}`}>
                      <PdcProposalDetail
                        key={item.staff_id}
                        data={item}
                        setProposalList={setProposalList}
                        isOnlyViewAgenda={isOnlyViewAgenda}
                        prevData={getProposalPrevValue(item)}
                      />
                    </div>
                  )}
                  {compareItem?.staff_id === item?.staff_id && (
                    <SuccessorComparison
                      isPopupTalentComparison={!isEmpty(compareItem)}
                      setIsPopupTalentComparison={setCompareItem}
                      currentTalent={compareItem}
                      setProposalList={setProposalList}
                      proposalList={proposalList}
                      isOnlyViewAgenda={isOnlyViewAgenda}
                      fetchProposalList={fetchProposalList}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>

        {isShowBotButtons && (
          <MobilityBotButtonControls
            idMeeting={idMeeting}
            mobilityId={mobilityId}
            isDisabled={false}
            handleSaveAsDraft={handleSaveAsDraft}
            mobilityDetails={mobilityDetails}
            fetchMobility={fetchMobilityDetails}
            handleClickNext={handleClickNext}
            handleClickBack={handleClickBack}
            isOnlyViewAgenda={isOnlyViewAgenda}
            setIsBack={setIsBack}
            isHRPartners={isHRPartners}
            isAdditionalViewAccess={isAdditionalViewAccess}
            isChairMainOrCommiteeOrAdvocator={isChairMainOrCommiteeOrAdvocator}
            meetingDetail={meetingDetail}
          />
        )}
      </Spin>

      <ModelTC
        info={{
          type: 'withoutSaving',
          visible: isShowPopupSaveAgenda,
          setVisible: setIsShowPopupSaveAgenda,
          onClose: handleKeepNavigate,
          handleSubmit: handleSaveBeforeExit,
        }}
      />
    </>
  );
};

export default PdcProposalList;
